import type { JSX } from 'react'
import type { ServerProbeDto } from '@/lib/dto'
import { clockOf } from '@/ui/format'
import { Chip } from '@/ui/primitives/Chip'

/**
 * Результат пробы сервера.
 *
 * Главное здесь не «онлайн/офлайн», а `supportedByCore`: именно оно спасает от
 * «создал бота на 26.3-snapshot и полчаса не понимал, почему он не входит».
 * Поэтому поддержка ядром — отдельный чип со своим текстом, а не мелкая
 * приписка к версии.
 *
 * Каждый чип несёт слово: по одному цвету «зелёный/жёлтый» отличать поддержку от
 * онлайна нельзя.
 */
export function ProbeBadge({
  probe,
  showTime = true
}: {
  probe: ServerProbeDto | null | undefined
  /** Время последней пробы. В диалоге не нужно: там проба всегда «только что». */
  showTime?: boolean
}): JSX.Element {
  if (probe === null || probe === undefined) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip tone="idle" dot>
          Пинга ещё не было
        </Chip>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Chip tone={probe.online ? 'live' : 'idle'} dot>
        {probe.online ? 'Онлайн' : 'Не отвечает'}
      </Chip>

      {probe.online ? (
        <>
          <Chip mono title="Версия протокола сервера">
            {probe.version ?? 'версия неизвестна'}
          </Chip>
          {probe.latencyMs === null ? null : <Chip mono>{probe.latencyMs} мс</Chip>}
          {probe.players === null ? null : (
            <Chip mono title="Игроков онлайн из максимума">
              {probe.players.online}/{probe.players.max}
            </Chip>
          )}
          <Chip tone={probe.supportedByCore ? 'live' : 'warn'} dot>
            {probe.supportedByCore ? 'Ядро поддерживает' : 'Ядро не знает эту версию'}
          </Chip>
        </>
      ) : null}

      {showTime ? (
        <span className="font-mono text-2xs text-ink-muted tabular-nums">
          проверено {clockOf(probe.at)}
        </span>
      ) : null}
    </div>
  )
}
